import { useEffect, useState } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { supabase } from "@/integrations/supabase/client";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Calendar, GraduationCap } from "lucide-react";
import { format } from "date-fns";
import { Skeleton } from "@/components/ui/skeleton";
import LinkifiedText from "@/components/LinkifiedText";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";

interface Announcement {
  id: string;
  title: string;
  content: string;
  created_at: string;
  linkedCourses?: { id: string; title: string }[];
}

const Announcements = () => {
  const { language } = useLanguage();
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);

  const content = {
    en: {
      title: "Church Announcements",
      subtitle: "Stay updated with the latest news and announcements from Good Gracious Church",
      noAnnouncements: "No announcements at this time.",
    },
    ru: {
      title: "Объявления Церкви",
      subtitle: "Будьте в курсе последних новостей и объявлений от церкви Good Gracious",
      noAnnouncements: "В настоящее время объявлений нет.",
    },
    es: {
      title: "Anuncios de la Iglesia",
      subtitle: "Mantente actualizado con las últimas noticias y anuncios de Good Gracious Church",
      noAnnouncements: "No hay anuncios en este momento.",
    },
  };

  useEffect(() => {
    fetchAnnouncements();
  }, [language]);

  const fetchAnnouncements = async () => {
    try {
      setLoading(true);
      
      // Fetch published announcements
      const { data: announcementsData, error } = await supabase
        .from("announcements")
        .select("*")
        .eq("published", true)
        .order("created_at", { ascending: false });

      if (error) throw error;

      if (!announcementsData) {
        setAnnouncements([]);
        return;
      }

      // Always fetch translations for the current language (including English)
      const { data: translations } = await supabase
        .from("announcements_translations")
        .select("*")
        .eq("language", language)
        .in("announcement_id", announcementsData.map(a => a.id));

      // Fetch linked courses for announcements
      const { data: courseLinks } = await supabase
        .from("announcement_courses")
        .select(`
          announcement_id,
          course_id,
          courses!inner(id, title, published)
        `)
        .in("announcement_id", announcementsData.map(a => a.id));

      const translationsMap = new Map(
        translations?.map(t => [t.announcement_id, t]) || []
      );

      // Group courses by announcement
      const coursesMap = new Map<string, { id: string; title: string }[]>();
      courseLinks?.forEach((link: any) => {
        if (link.courses && link.courses.published) {
          if (!coursesMap.has(link.announcement_id)) {
            coursesMap.set(link.announcement_id, []);
          }
          coursesMap.get(link.announcement_id)!.push({
            id: link.courses.id,
            title: link.courses.title,
          });
        }
      });

      const translatedAnnouncements = announcementsData.map(announcement => {
        const translation = translationsMap.get(announcement.id);
        return {
          id: announcement.id,
          title: translation?.title || announcement.title,
          content: translation?.content || announcement.content,
          created_at: announcement.created_at,
          linkedCourses: coursesMap.get(announcement.id) || [],
        };
      });

      setAnnouncements(translatedAnnouncements);
    } catch (error) {
      console.error("Error fetching announcements:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-grow pt-20">
        <section className="py-16 bg-gradient-to-b from-background to-muted/20">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h1 className="text-4xl md:text-5xl font-bold mb-4">
                {content[language].title}
              </h1>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                {content[language].subtitle}
              </p>
            </div>

            <div className="max-w-4xl mx-auto space-y-6">
              {loading ? (
                <>
                  <Skeleton className="h-48 w-full" />
                  <Skeleton className="h-48 w-full" />
                  <Skeleton className="h-48 w-full" />
                </>
              ) : announcements.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <p className="text-muted-foreground">
                      {content[language].noAnnouncements}
                    </p>
                  </CardContent>
                </Card>
              ) : (
                announcements.map((announcement) => (
                  <Card key={announcement.id} className="hover:shadow-lg transition-shadow">
                    <CardHeader>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                        <Calendar className="h-4 w-4" />
                        <time dateTime={announcement.created_at}>
                          {format(new Date(announcement.created_at), "MMMM d, yyyy")}
                        </time>
                      </div>
                      <CardTitle className="text-2xl">{announcement.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <LinkifiedText 
                        text={announcement.content}
                        className="text-base whitespace-pre-wrap text-muted-foreground"
                      />
                      
                      {announcement.linkedCourses && announcement.linkedCourses.length > 0 && (
                        <div className="mt-4 pt-4 border-t">
                          <div className="flex items-center gap-2 text-sm font-semibold mb-2">
                            <GraduationCap className="h-4 w-4" />
                            {language === 'en' ? 'Related Courses:' :
                             language === 'ru' ? 'Связанные курсы:' :
                             'Cursos Relacionados:'}
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {announcement.linkedCourses.map((course) => (
                              <Link key={course.id} to="/bible-school">
                                <Badge variant="secondary" className="cursor-pointer hover:bg-primary hover:text-primary-foreground transition-colors">
                                  {course.title}
                                </Badge>
                              </Link>
                            ))}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Announcements;
